import { useState, useEffect, useRef } from 'react';
import {
  getReviewQueue,
  getReviewJobDetail,
  resolveAugmentationJob
} from '../services/api/augmentationReviewApi';
import { getCustomersForDropdown } from '../services/api/customerApi';

export const useAugmentationReview = () => {
  const [reviewQueue, setReviewQueue] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const hasFetchedCustomers = useRef(false);

  // Fetch customers on mount
  useEffect(() => {
    if (!hasFetchedCustomers.current) {
      hasFetchedCustomers.current = true;
      fetchCustomers();
    }
  }, []);

  // Fetch review queue when customer changes
  useEffect(() => {
    fetchReviewQueue();
  }, [selectedCustomerId]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomersForDropdown();
      const customerList = response.data.items || response.data || [];
      setCustomers(customerList);
    } catch (err) {
      setError('Failed to fetch customers');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewQueue = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getReviewQueue(selectedCustomerId || null);
      setReviewQueue(response.data || []);
    } catch (err) {
      setError('Failed to fetch review queue');
      console.error('Error fetching review queue:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobDetail = async (jobId) => {
    try {
      setError(null);
      const response = await getReviewJobDetail(jobId);
      return response.data;
    } catch (err) {
      setError('Failed to fetch job details');
      console.error('Error fetching job details:', err);
      throw err;
    }
  };

  const resolveJob = async (jobId, resolution) => {
    try {
      setError(null);
      await resolveAugmentationJob(jobId, resolution);
      setSuccess('Job resolved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      await fetchReviewQueue();
    } catch (err) {
      setError('Failed to resolve job');
      console.error('Error resolving job:', err);
      throw err;
    }
  };

  return {
    reviewQueue,
    customers,
    loading,
    error,
    success,
    selectedCustomerId,
    setSelectedCustomerId,
    fetchReviewQueue,
    fetchJobDetail,
    resolveJob,
  };
};
